import { useEffect, useState } from 'react';

import axios from 'axios';
import classNames from 'classnames';
import { addDays, format, isSaturday, isSunday, isToday } from 'date-fns';
import { useSelector } from 'hooks';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';

import { PlanUnitMix } from 'components/types';

import { getPlanunitCommaIds } from 'components/utils';
import { formatByLocale } from 'utils/date';

import { Entry, getData } from 'RestAPIs/quality-monitor';

import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

type Loading = {
  id: number;
  date: Date;
};

const SlicedTable = ({ data, start, end, openRoster }: any) => {
  const [loading, setLoading] = useState<Loading | null>(null);
  const onOpenRoster = (id, date?) => {
    openRoster(id, date, true);
    setLoading({ id, date });
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {data[0].days.slice(start, end).map(({ date }) => (
              <TableCell
                key={date}
                align="center"
                className={classNames({
                  'is-today': isToday(date),
                  'is-saturday': isSaturday(date),
                  'is-sunday': isSunday(date)
                })}
              >
                <div>
                  <b>{formatByLocale(date, 'EEEEEE')}</b>
                </div>
                <div>{format(date, 'dd')}</div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map(({ costCenterId, days }) => (
            <TableRow key={costCenterId}>
              {days.slice(start, end).map(({ date, totalNumberOfActions }) => (
                <TableCell
                  key={date}
                  align="center"
                  className={classNames({
                    'is-today': isToday(date),
                    'is-saturday': isSaturday(date),
                    'is-sunday': isSunday(date)
                  })}
                >
                  {totalNumberOfActions > 0 ? (
                    <Chip
                      variant="outlined"
                      onClick={() => onOpenRoster(costCenterId, date)}
                      label={
                        loading &&
                        loading.id === costCenterId &&
                        loading.date === date ? (
                          <i className="icon-spinner icon-spin" />
                        ) : (
                          totalNumberOfActions
                        )
                      }
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

type Props = {
  setIsLoading: (state: boolean) => void;
  selectedPlanUnits: PlanUnitMix[];
};

export default function OpenRequests({
  setIsLoading,
  selectedPlanUnits
}: Props) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<Entry[]>([]);
  const [loadingPlanUnitID, setLoadingPlanUnitID] = useState(0);
  const planUnits = useSelector((state) => state.planUnits);

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    if (selectedPlanUnits.length) {
      const costCenterIds = getPlanunitCommaIds(selectedPlanUnits);
      if (!costCenterIds) {
        setData([]);
        return noop;
      }

      const today = new Date();
      const lastDay = addDays(today, 8);
      const params = {
        costCenterIds,
        startDate: format(today, 'yyyy-MM-dd'),
        endDate: format(lastDay, 'yyyy-MM-dd')
      };

      setIsLoading(true);
      getData({ params, cancelToken: source.token }).then(
        ({ data }) => {
          data.forEach(({ days }) =>
            days.forEach((day) => {
              day.date = new Date(day.date);
            })
          );
          setData(data);
          setIsLoading(false);
        },
        (err) => {
          if (!(err && err.message === 'unmount')) {
            setIsLoading(false);
          }
        }
      );
    } else {
      setData([]);
    }

    return () => source.cancel('unmount');
  }, [selectedPlanUnits, setIsLoading]);

  const openRoster = (selectedPlanUnitId, date?, isWeek4View?) => {
    date = date || new Date();
    date.setHours(0, 0, 0, 0);

    window.dojoTopic.publish(
      'setRosterTempPlanunits',
      planUnits.filter(({ id }) => selectedPlanUnitId === id),
      date,
      isWeek4View
    );
    setLoadingPlanUnitID(selectedPlanUnitId);
    // TODO: remove loaders after ROSTER migration is done
  };

  if (!data.length) {
    return <></>;
  }

  return (
    <Paper className="open-requests">
      <h2>{t('openRequests')}</h2>
      <div className="d-flex">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="row">
                  <div>&nbsp;</div>
                  <div>
                    <b>{t('planUnits')}</b>
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(({ costCenterId, costCenterName }) => (
                <TableRow key={costCenterId}>
                  <TableCell component="th" scope="row">
                    <button
                      type="button"
                      className="plain-btn"
                      onClick={() => openRoster(costCenterId, null, true)}
                    >
                      {costCenterName}
                    </button>
                    {loadingPlanUnitID === costCenterId && (
                      <i className="icon-spinner icon-spin" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <SlicedTable data={data} start="0" end="1" openRoster={openRoster} />
        <SlicedTable data={data} start="1" end="8" openRoster={openRoster} />
      </div>
    </Paper>
  );
}
